import cs from "classnames";
import React from "react";
import { injectIntl, WrappedComponentProps as IntlProps } from "react-intl";

import { genericMessages } from "src/messages/generic";

import "./SearchMenu.css";

interface SearchMenuProps<T> extends IntlProps {
	isMenuOpen: boolean;
	onCloseMenu(): unknown;
	items: T[];
	noItemsMessage: React.ReactNode;
	itemPredicate(item: T, query: string): boolean;
	getItemKey(item: T, index: number): string;
	renderItem(item: T): React.ReactNode;
	onSelectItem(item: T): unknown;
}

interface StateProps<T> {
	query: string;
	index: number;
	items: T[];
}

class BareSearchMenu<T> extends React.PureComponent<
	SearchMenuProps<T>,
	StateProps<T>
> {
	private menuRef = React.createRef<HTMLDivElement>();
	private queryRef = React.createRef<HTMLInputElement>();

	constructor(props: SearchMenuProps<T>) {
		super(props);
		this.state = { query: "", index: 0, items: this.props.items };
	}

	componentDidMount(): void {
		document.addEventListener("mousedown", this.onClickOutside);
	}

	componentWillUnmount(): void {
		document.removeEventListener("mousedown", this.onClickOutside);
	}

	componentDidUpdate(
		prevProps: SearchMenuProps<T>,
		prevState: StateProps<T>
	): void {
		if (
			this.props.items !== prevProps.items ||
			this.state.query !== prevState.query
		) {
			this.filterItems();
		}
		if (this.props.isMenuOpen && !prevProps.isMenuOpen) {
			this.onOpen();
		}
	}

	render(): React.ReactNode {
		const { isMenuOpen } = this.props;
		return (
			<div
				ref={this.menuRef}
				className={cs("search-menu", {
					"search-menu--open": isMenuOpen
				})}
			>
				{this.renderQuery()}
				{this.renderList()}
			</div>
		);
	}

	private renderQuery(): React.ReactElement {
		const {
			intl: { formatMessage }
		} = this.props;
		const { query } = this.state;
		return (
			<input
				ref={this.queryRef}
				type="text"
				className={cs("search-menu__query", "field__ignore-blur-event")}
				placeholder={formatMessage(genericMessages.search)}
				value={query}
				onChange={this.onChangeQuery}
				onKeyDown={this.onKeyDownQuery}
			/>
		);
	}

	private renderList(): React.ReactElement {
		const { noItemsMessage } = this.props;
		const { items } = this.state;
		return items.length === 0 ? (
			<div className="search-menu__message">{noItemsMessage}</div>
		) : (
			<div className="search-menu__list">
				{items.map(this.renderItem)}
			</div>
		);
	}

	private renderItem = (
		item: T,
		currentIndex: number
	): React.ReactElement => {
		const { getItemKey, renderItem, onSelectItem } = this.props;
		const { index } = this.state;
		return (
			<SearchMenuItem
				key={getItemKey(item, index)}
				item={item}
				render={renderItem}
				isSelected={currentIndex === index}
				onSelect={onSelectItem}
			/>
		);
	};

	private onClickOutside = (event: MouseEvent): void => {
		if (!this.props.isMenuOpen) {
			return;
		}
		if (this.menuRef.current?.contains(event.target as Element)) {
			return;
		}
		this.props.onCloseMenu();
	};

	private filterItems(): void {
		this.setState({
			items: this.props.items.filter(item =>
				this.props.itemPredicate(item, this.state.query)
			)
		});
	}

	private onOpen(): void {
		this.setState({ query: "" });
		window.requestAnimationFrame(this.focusOnQuery);
	}

	private focusOnQuery = (): void => {
		if (this.queryRef.current !== null) {
			this.queryRef.current.focus();
		}
	};

	private onChangeQuery = (
		event: React.ChangeEvent<HTMLInputElement>
	): void => {
		this.setState({ query: event.target.value, index: 0 });
	};

	private onKeyDownQuery = (event: React.KeyboardEvent): void => {
		if (event.key === "ArrowUp") {
			return this.onKeyDownArrowUp();
		}
		if (event.key === "ArrowDown") {
			return this.onKeyDownArrowDown();
		}
		if (event.key === "Enter") {
			return this.onKeyDownEnter(event);
		}
	};

	private onKeyDownArrowUp(): void {
		this.setState(({ index }) => ({ index: Math.max(index - 1, 0) }));
	}

	private onKeyDownArrowDown(): void {
		this.setState(({ index }) => ({
			index: Math.min(index + 1, this.state.items.length - 1)
		}));
	}

	private onKeyDownEnter(event: React.KeyboardEvent): void {
		event.preventDefault();
		this.props.onSelectItem(this.state.items[this.state.index]);
	}
}

interface SearchMenuItemProps<T> {
	item: T;
	render(item: T): React.ReactNode;
	isSelected: boolean;
	onSelect(item: T): unknown;
}

export class SearchMenuItem<T> extends React.PureComponent<
	SearchMenuItemProps<T>
> {
	private ref = React.createRef<HTMLDivElement>();

	componentDidUpdate(prevProps: SearchMenuItemProps<T>): void {
		if (this.props.isSelected && !prevProps.isSelected) {
			this.scrollIntoView();
		}
	}

	render(): React.ReactNode {
		const { item, render, isSelected } = this.props;
		return (
			<div
				ref={this.ref}
				className={cs("search-menu__item", {
					"search-menu__item--selected": isSelected
				})}
				onClick={this.onClick}
			>
				{render(item)}
			</div>
		);
	}

	private scrollIntoView(): void {
		const { current: el } = this.ref;
		if (el === null || el.scrollIntoView === undefined) {
			return;
		}
		el.scrollIntoView({ behavior: "smooth" });
	}

	private onClick = (): void => {
		this.props.onSelect(this.props.item);
	};
}

export const SearchMenu = injectIntl(BareSearchMenu);
